import { useCallback } from "react";
import { useAuthApi } from "../../infra/auth";
import {
  useAppDispatch,
  useAppSelector,
} from "../../presentation/hooks/store-hooks";
import {
  setLoginFormLoginClienteState,
  setLoginFormLoginUsuarioState,
  setLoginFormSenhaUsuarioState,
  setTokenState,
  setSessionConfig,
  resetSessionTimer as resetSessionTimerAction,
} from "../../application/states/auth";
import { useLocalStorageService } from "../local-storage";
import { decodeJwt } from "jose";
import { useUserApi } from "../../infra/user";
import {
  setCurrentAccessState,
  setCurrentUserState,
} from "../../application/states/user";
import { useUIService } from "../ui";

export const useAuthService = () => {
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const { loginForm, token } = useAppSelector((state) => state.auth);
  const { currentUser } = useAppSelector((state) => state.user);
  // API
  const { insertAcessoApi, refreshApi, fetchSessionConfigApi } = useAuthApi();
  const { getUserByIdApi } = useUserApi();
  // Use Case / Service
  const { setItem, getItem, removeItem } = useLocalStorageService();
  const { setAlert, setLoading } = useUIService();

  // Métodos
  const logout = useCallback((options?: { isIpBlock?: boolean; message?: string; redirectToModules?: boolean }) => {
    dispatch(setTokenState(""));
    dispatch(resetSessionTimerAction());
    removeItem({ key: "ACCESS_TOKEN" });
    removeItem({ key: "LOGIN_TIMESTAMP" });
    window.localStorage.clear();
    window.sessionStorage.clear();

    let loginUrl = String(process.env.REACT_APP_VELES_LOGIN_BASE_URL);
    if (!loginUrl.includes('/authentication/login')) {
      loginUrl = loginUrl.replace('/auth/login', '/authentication/login');
    }

    if (options?.message) {
      window.location.href = `${loginUrl}?message=${encodeURIComponent(options.message)}`;
    } else if (options?.redirectToModules) {
      const appBaseUrl = loginUrl.replace('/authentication/login', '').replace('/auth/login', '');
      window.location.href = `${appBaseUrl}/modulos`;
    } else {
      window.location.href = `${loginUrl}?logout=true`;
    }
  }, [dispatch, removeItem]);


  const fetchSessionConfig = useCallback(async () => {
    try {
      const response = await fetchSessionConfigApi();
      const { sessionTimeoutSeconds } = response;
      dispatch(setSessionConfig({
        timeout: sessionTimeoutSeconds
      }));
      return sessionTimeoutSeconds;
    } catch (error) {
      return Number(process.env.SESSION_TIMEOUT_SECONDS);
    }
  }, [dispatch, fetchSessionConfigApi]);

  const refresh = useCallback(async () => {
    try {
      const tokenStorage = getItem({
        key: "ACCESS_TOKEN",
      });
      if (!tokenStorage || tokenStorage === "not_valid" || tokenStorage === "") return;

      const response = await refreshApi(tokenStorage);
      const autenticado = response.Autenticado ?? (response as any).autenticado;
      const novoToken = response.Token ?? (response as any).token;
      const mensagem = response.Mensagem ?? (response as any).mensagem;

      if (novoToken && novoToken !== '') {
        // Só atualiza se chegou um token válido
        dispatch(setTokenState(novoToken));
        setItem({
          key: "ACCESS_TOKEN",
          value: novoToken,
        });
      } else if (autenticado === false) {
        // Só faz logout se explicitamente rejeitado pelo servidor
        const isIpBlock = Boolean(mensagem && typeof mensagem === 'string' && mensagem.toLowerCase().includes("ip"));
        if (!isIpBlock) {
          logout();
        }
      }
    } catch (error: any) {
      const resData = error?.response?.data || {};
      const msg = resData.message || resData.error || resData.Mensagem || resData.mensagem || "";
      const isIpBlock = Boolean(msg && typeof msg === 'string' && msg.toLowerCase().includes("ip"));
      if (!isIpBlock && error?.response?.status === 401) {
        logout();
      }
    }
  }, [dispatch, getItem, refreshApi, setItem, logout]);

  const decodeToken = useCallback((data: { token: string }) => {
    const decoded = decodeJwt(data.token);

    return decoded;
  }, []);

  const setupSession = useCallback(async () => {
    try {
      const tokenStorage = getItem({
        key: "ACCESS_TOKEN",
      });
      const timestamp = getItem({ key: "LOGIN_TIMESTAMP" });

      if (tokenStorage && tokenStorage !== "" && tokenStorage !== "not_valid") {
        dispatch(setTokenState(tokenStorage));

        if (timestamp) {
          dispatch(setSessionConfig({
            timeout: Number(process.env.SESSION_TIMEOUT_SECONDS),
            timestamp: Number(timestamp)
          }));
        } else {
          const now = Date.now();
          setItem({ key: "LOGIN_TIMESTAMP", value: String(now) });
          dispatch(setSessionConfig({
            timeout: Number(process.env.SESSION_TIMEOUT_SECONDS),
            timestamp: now
          }));
        }

        await fetchSessionConfig();

        const decoded = decodeToken({
          token: tokenStorage,
        });

        const rawUserId = decoded.IdUsuario ?? decoded.idUsuario;
        const userId = rawUserId ? Number(rawUserId) : undefined;

        if (userId && !isNaN(userId)) {
          const user = await getUserByIdApi({
            IdUsuario: userId,
          });
          dispatch(setCurrentUserState(user.usuario));
        }
      } else {
        dispatch(setTokenState("not_valid"));
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        const tokenStorage = getItem({
          key: "ACCESS_TOKEN",
        });
        if (tokenStorage && tokenStorage !== "") {
          dispatch(setTokenState("not_valid"));
          removeItem({
            key: "ACCESS_TOKEN",
          });
          setAlert({
            message: "Sessão expirada. Faça login novamente.",
            type: "error",
          });
        }
      } else {
        setAlert({
          message: error.response?.data?.error || "Erro ao carregar sessão.",
          type: "error",
        });
      }
    }
  }, [decodeToken, dispatch, getItem, getUserByIdApi, removeItem, setAlert, setItem, fetchSessionConfig]);

  //Login Form Actions
  const setLoginFormLoginCliente = useCallback(
    (loginCliente: string) => {
      dispatch(setLoginFormLoginClienteState({ loginCliente }));
    },
    [dispatch],
  );

  const setLoginFormLoginUsuario = useCallback(
    (loginUsuario: string) => {
      dispatch(setLoginFormLoginUsuarioState({ loginUsuario }));
    },
    [dispatch],
  );

  const setLoginFormSenhaUsuario = useCallback(
    (senhaUsuario: string) => {
      dispatch(setLoginFormSenhaUsuarioState({ senhaUsuario }));
    },
    [dispatch],
  );

  const insertAcesso = useCallback(
    async (idProduto: number) => {
      try {
        const tokenStorage = getItem({
          key: "ACCESS_TOKEN",
        });

        if (tokenStorage) {
          dispatch(setTokenState(tokenStorage));

          const decoded = decodeToken({
            token: tokenStorage,
          });

          const idUsuario = decoded.IdUsuario
            ? (decoded.IdUsuario as number)
            : undefined;

          if (idUsuario) {
            const insertAcesso = await insertAcessoApi({
              IdProduto: idProduto,
              IdUsuario: idUsuario,
            });

            dispatch(setTokenState(insertAcesso.token));
            dispatch(setCurrentUserState(insertAcesso.usuario));
            dispatch(setCurrentAccessState(insertAcesso.acesso));

            const timestamp = Date.now();
            setItem({
              key: "LOGIN_TIMESTAMP",
              value: String(timestamp),
            });
            dispatch(setSessionConfig({
              timeout: Number(process.env.SESSION_TIMEOUT_SECONDS),
              timestamp: timestamp
            }));
            await fetchSessionConfig();

            removeItem({
              key: "ACCESS_TOKEN",
            });

            setItem({
              key: "ACCESS_TOKEN",
              value: insertAcesso.token,
            });
          }
        }
      } catch (error: any) {
        setAlert({
          message: error.response.data.error,
          type: "error",
        });
      }
    },
    [
      decodeToken,
      dispatch,
      getItem,
      insertAcessoApi,
      removeItem,
      setAlert,
      setItem,
      fetchSessionConfig,
    ],
  );

  const setToken = useCallback(() => {
    const token = getItem({
      key: "ACCESS_TOKEN",
    });

    if (token) {
      dispatch(setTokenState(token));
    }
  }, [dispatch, getItem]);

  const resetSessionTimer = useCallback(() => {
    const now = Date.now();
    setItem({
      key: "LOGIN_TIMESTAMP",
      value: String(now),
    });
    dispatch(
      setSessionConfig({
        timeout: Number(process.env.SESSION_TIMEOUT_SECONDS),
        timestamp: now,
      })
    );
  }, [dispatch, setItem]);

  return {
    // Login Form Actions
    setLoginFormLoginCliente,
    setLoginFormSenhaUsuario,
    setLoginFormLoginUsuario,
    insertAcesso,
    setupSession,
    logout,
    setToken,
    refresh,
    resetSessionTimer,
  };
};
